import { DynamicPriceRuleSet } from '@modules/bcm/products/product/dynamic-price-rules-builder/models/dynamic-price-rules.interfaces';
import { DynamicPriceCalculationSet } from '@modules/bcm/products/product/dynamic-price-calculations-builder/models/dynamic-price-calculations.interfaces';
import {
    ConditionNode,
    ConditionNodeType,
    DynamicPriceGroup,
    GroupCombineOperator,
    LegacySnapshot,
} from '@shared/models/dynamic-price-groups.interfaces';
import { DynamicPriceRulesEntityId } from '@modules/bcm/products/product/dynamic-price-rules-builder/models/dynamic-price-rules-entity-id';
import { DynamicPriceRulesQueryOperator } from '@modules/bcm/products/product/dynamic-price-rules-builder/models/dynamic-price-rules-query-operator';

export interface BcmProductDynamicPriceMapDto {
    id?: number;
    ruleSets?: DynamicPriceRuleSet[];
    calculationSets: DynamicPriceCalculationSet[];
    // Groups model (v2). Presence (!= null) signals the converted shape.
    groups?: DynamicPriceGroup[];
    groupCombineOperator?: GroupCombineOperator;
    legacySnapshot?: LegacySnapshot;
}

export class BcmProductDynamicPriceMap {

    id: number;
    ruleSets: DynamicPriceRuleSet[];
    calculationSets: DynamicPriceCalculationSet[];
    groups?: DynamicPriceGroup[];
    groupCombineOperator?: GroupCombineOperator;
    legacySnapshot?: LegacySnapshot;

    constructor(dto = {} as BcmProductDynamicPriceMapDto) {
        this.id = dto?.id;
        this.ruleSets = dto?.ruleSets ? dto?.ruleSets : [];
        this.calculationSets = dto?.calculationSets ? dto?.calculationSets : [];
        // Preserve null/undefined distinction — drives shape-dispatch downstream.
        this.groups = dto?.groups;
        this.groupCombineOperator = dto?.groupCombineOperator;
        this.legacySnapshot = dto?.legacySnapshot;
    }

    getRequirements(): string[] {
        const ruleRequirements = (this.ruleSets || [])
            .flatMap(ruleSet => ruleSet.rules.flatMap(rule => rule.entityId))
            .filter((v, i, a) => a.indexOf(v) === i);

        const calculationRequirements = (this.calculationSets || [])
            .flatMap(calculationSet => (calculationSet.calculations || []).flatMap(calculation => calculation.entityId))
            .filter((v, i, a) => a.indexOf(v) === i);

        // v2: walk condition leaves + factor entityIds so the booking dialog still prompts for
        // boat/berth/etc on converted products.
        const groupRequirements = collectGroupEntityIds(this.groups || []);

        return [...new Set([...ruleRequirements, ...calculationRequirements, ...groupRequirements])];
    }

    /**
     * Tenant relation ids the price rules name as a criterion, i.e. the relations this product is
     * priced for. Callers use it to restrict the relation selector so a user cannot pick a relation
     * that no rule names (which would leave the dynamic price unresolved).
     *
     * Only a *positive* equality names a relation. A negative criterion says which relation a single
     * alternative rules out — it must never remove a relation, because another alternative may well
     * price it. This matters concretely: the legacy→groups converter (convert-legacy-to-groups.ts in
     * the API) preserves first-match semantics by guarding every later group with NOT(prior
     * conditions), so on a converted product the very relation group 1 prices also appears negated in
     * group 2. Treating that as an exclusion would delete exactly the relations the product prices.
     *
     * An empty result means "no relation criteria" → no restriction.
     *
     * tenantRelation cannot appear in calculationSets/factors (see DynamicPriceCalculationsEntityId),
     * so only rules and group conditions are walked.
     */
    getTenantRelationCriteriaIds(): number[] {
        const relevant = new Set<number>();

        const collect = (entityId: string, operator: string, value: any, negated = false): void => {
            if (entityId !== DynamicPriceRulesEntityId.TenantRelation) {
                return;
            }

            const isEqual = operator === DynamicPriceRulesQueryOperator.Equal;
            const isNotEqual = operator === DynamicPriceRulesQueryOperator.NotEqual;

            // Ordering/contains operators are meaningless on a relation select — they name nothing.
            if (!isEqual && !isNotEqual) {
                return;
            }

            // `=` under a negation means "not this relation", `!=` under one means "this relation".
            const namesRelation = isEqual !== negated;

            if (!namesRelation) {
                return;
            }

            // Rule values arrive as number or string depending on where they were persisted. A rule
            // saved before an option was picked holds null/'' — Number() would turn that into id 0,
            // an allow-list matching no relation at all.
            (Array.isArray(value) ? value : [value])
                .map(v => Number(v))
                .filter(id => Number.isInteger(id) && id > 0)
                .forEach(id => relevant.add(id));
        };

        for (const ruleSet of (this.ruleSets || [])) {
            for (const rule of (ruleSet.rules || [])) {
                collect(rule.entityId, rule.operator, rule.value);
            }
        }

        for (const group of (this.groups || [])) {
            collectConditionRelationCriteria(group.condition, false, collect);
        }

        return [...relevant];
    }

}

/**
 * Walks a v2 condition tree and reports every leaf to `collect`. A negated branch flips the meaning
 * of everything below it, so the negation is carried down rather than evaluated per leaf.
 */
function collectConditionRelationCriteria(
    node: ConditionNode | null,
    negated: boolean,
    collect: (entityId: string, operator: string, value: any, negate?: boolean) => void,
): void {
    if (!node) {
        return;
    }

    const isNegated = negated !== !!node.negate;

    if (node.type === ConditionNodeType.Leaf) {
        collect(node.entityId, node.operator, node.value, isNegated);
        return;
    }

    for (const child of (node.children || [])) {
        collectConditionRelationCriteria(child, isNegated, collect);
    }
}

function collectGroupEntityIds(groups: DynamicPriceGroup[]): string[] {
    const ids: string[] = [];
    for (const group of groups) {
        collectConditionEntityIds(group.condition as any, ids);
        for (const f of group.factors || []) {
            if (f.entityId) ids.push(f.entityId);
        }
    }
    return ids;
}

function collectConditionEntityIds(node: any, out: string[]): void {
    if (!node) return;
    if (node.type === 'leaf') {
        if (node.entityId) out.push(node.entityId);
        return;
    }
    for (const child of (node.children || [])) {
        collectConditionEntityIds(child, out);
    }
}
